<template>
  <div class="developer-integrations mb-5">
    <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
      <div>
        <h4 class="mb-1">Developer &amp; AI integrations</h4>
        <p class="text-muted mb-0">Connect Gstocky securely to ChatGPT, Claude, APIs, and webhook automations.</p>
      </div>
      <span class="badge bg-light text-dark border px-3 py-2">MCP · API v1</span>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
    </div>

    <template v-else>
      <div class="card border mcp-card mb-4">
        <div class="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div class="d-flex align-items-center gap-3">
            <div class="brand-stack" aria-hidden="true">
              <connector-brand-icon brand="chatgpt" :size="40" />
              <connector-brand-icon brand="claude" :size="40" />
              <connector-brand-icon brand="gstocky" :size="40" />
            </div>
            <div>
              <strong>Chat with Gstocky</strong>
              <div class="small text-muted mt-1">Connect ChatGPT or Claude to create invoices and quotes from a conversation.</div>
            </div>
          </div>
          <span class="badge mcp-badge px-3 py-2">MCP · OAuth</span>
        </div>
        <div class="card-body">
          <label for="mcp-server-url" class="form-label fw-bold">Gstocky MCP link</label>
          <div class="input-group mcp-url-group">
            <span class="input-group-text bg-white"><AppIcon name="bi-link-45deg" class="bi bi-link-45deg" /></span>
            <input id="mcp-server-url" class="form-control font-monospace" readonly :value="mcpUrl" @focus="$event.target.select()">
            <button class="btn btn-primary" type="button" @click="copy(mcpUrl)">
              <AppIcon name="bi-copy" class="bi bi-copy me-1" />Copy MCP link
            </button>
          </div>
          <p class="small text-muted mt-2 mb-4">Authentication happens through Gstocky OAuth. Never paste an API token or password into ChatGPT or Claude.</p>

          <div class="connector-guides">
            <article class="connector-guide">
              <div class="connector-guide-heading">
                <connector-brand-icon brand="chatgpt" :size="42" />
                <div><h5 class="mb-0">Connect ChatGPT</h5><span class="small text-muted">ChatGPT web · workspace permissions may apply</span></div>
              </div>
              <ol class="connection-steps">
                <li><span>1</span><div>Open <strong>ChatGPT → Plugins</strong>.</div></li>
                <li><span>2</span><div>Select <strong>+</strong>, then <strong>Add custom MCP server</strong>.</div></li>
                <li><span>3</span><div>Name it <strong>Gstocky</strong>, paste the MCP link, and choose <strong>OAuth</strong>.</div></li>
                <li><span>4</span><div>Accept the risk notice, select <strong>Create as a plugin</strong>, then sign in to Gstocky and approve access.</div></li>
              </ol>
            </article>

            <article class="connector-guide">
              <div class="connector-guide-heading">
                <connector-brand-icon brand="claude" :size="42" />
                <div><h5 class="mb-0">Connect Claude</h5><span class="small text-muted">Claude web, Desktop, and mobile</span></div>
              </div>
              <ol class="connection-steps">
                <li><span>1</span><div>Open <strong>Claude → Customize → Connectors</strong>.</div></li>
                <li><span>2</span><div>Select <strong>+ Add → Custom → Web</strong>.</div></li>
                <li><span>3</span><div>Name it <strong>Gstocky</strong>, paste the MCP link, and select <strong>Register automatically</strong> for OAuth.</div></li>
                <li><span>4</span><div>Add the connector, select <strong>Connect</strong>, then sign in to Gstocky and approve access.</div></li>
              </ol>
            </article>
          </div>

          <div class="mcp-capabilities mt-4">
            <span><AppIcon name="bi-people" class="bi bi-people" />Find clients</span>
            <span><AppIcon name="bi-box-seam" class="bi bi-box-seam" />Find products</span>
            <span><AppIcon name="bi-receipt" class="bi bi-receipt" />Create pending invoices</span>
            <span><AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text" />Create pending quotes</span>
            <span><AppIcon name="bi-file-earmark-pdf" class="bi bi-file-earmark-pdf" />Preview and download PDFs</span>
          </div>
        </div>
      </div>

      <div class="card border mb-4">
        <div class="card-header bg-white d-flex justify-content-between align-items-center">
          <div>
            <strong><AppIcon name="bi-key" class="bi bi-key me-2" />API tokens</strong>
            <div class="small text-muted mt-1">Tokens are hashed and the full value is shown only once.</div>
          </div>
          <button class="btn btn-primary btn-sm" @click="showTokenForm = !showTokenForm">
            <AppIcon name="bi-plus" class="bi bi-plus me-1" />Generate token
          </button>
        </div>
        <div class="card-body">
          <div v-if="newToken" class="alert alert-warning">
            <div class="fw-bold mb-1">Copy this token now. You will not be able to see it again.</div>
            <div class="input-group">
              <input class="form-control font-monospace" readonly :value="newToken">
              <button class="btn btn-outline-dark" @click="copy(newToken)">Copy</button>
            </div>
          </div>

          <form v-if="showTokenForm" class="integration-form rounded border bg-light p-3 mb-4" @submit.prevent="createToken">
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label">Name</label>
                <input v-model.trim="tokenForm.name" maxlength="100" required class="form-control" placeholder="Shopify connector">
              </div>
              <div class="col-md-3">
                <label class="form-label">Requests per minute</label>
                <input v-model.number="tokenForm.rate_limit_per_minute" type="number" min="10" max="1000" required class="form-control">
              </div>
              <div class="col-md-3">
                <label class="form-label">Expires (optional)</label>
                <input v-model="tokenForm.expires_at" type="date" class="form-control">
              </div>
              <div class="col-md-2 d-flex align-items-end">
                <div class="form-check mb-2">
                  <input id="token-write-scope" v-model="tokenForm.write" type="checkbox" class="form-check-input">
                  <label for="token-write-scope" class="form-check-label">Write access</label>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-end gap-2 mt-3">
              <button type="button" class="btn btn-light border" @click="showTokenForm = false">Cancel</button>
              <button class="btn btn-success" :disabled="savingToken">
                <span v-if="savingToken" class="spinner-border spinner-border-sm me-2"></span>Generate
              </button>
            </div>
          </form>

          <div v-if="tokens.length" class="table-responsive">
            <table class="table align-middle mb-0">
              <thead><tr><th>Name</th><th>Token</th><th>Scopes</th><th>Limit</th><th>Last used</th><th>Status</th><th></th></tr></thead>
              <tbody>
                <tr v-for="token in tokens" :key="token.id">
                  <td class="fw-medium">{{ token.name }}</td>
                  <td><code>{{ token.prefix }}...</code></td>
                  <td><span v-for="scope in token.scopes" :key="scope" class="badge bg-light text-dark border me-1">{{ scope }}</span></td>
                  <td>{{ token.rate_limit_per_minute }}/min</td>
                  <td class="small text-muted">{{ formatDate(token.last_used_at) }}</td>
                  <td>
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" :checked="token.enabled" @change="toggleToken(token, $event.target.checked)">
                    </div>
                  </td>
                  <td class="text-end"><button class="btn btn-outline-danger btn-sm" @click="revokeToken(token)">Revoke</button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-muted mb-0">No API tokens have been generated.</p>

          <div class="api-example mt-4 p-3 rounded border">
            <div class="small fw-bold mb-2">Example request</div>
            <code>curl {{ apiBase }}/clients -H "Authorization: Bearer YOUR_TOKEN"</code>
            <div class="small text-muted mt-2">Available resources: clients, products, orders, invoices, suppliers, and purchases. Responses include rate-limit headers; page size is capped at 100 and request bodies at 1 MB.</div>
          </div>
        </div>
      </div>

      <div class="card border">
        <div class="card-header bg-white d-flex justify-content-between align-items-center">
          <div>
            <strong><AppIcon name="bi-broadcast" class="bi bi-broadcast me-2" />Outbound webhooks</strong>
            <div class="small text-muted mt-1">GStocky signs every event and retries failed deliveries up to three times.</div>
          </div>
          <button class="btn btn-primary btn-sm" @click="showWebhookForm = !showWebhookForm">
            <AppIcon name="bi-plus" class="bi bi-plus me-1" />Add endpoint
          </button>
        </div>
        <div class="card-body">
          <div v-if="newWebhookSecret" class="alert alert-warning">
            <div class="fw-bold mb-1">Copy the signing secret now. It is used to verify webhook signatures.</div>
            <div class="input-group">
              <input class="form-control font-monospace" readonly :value="newWebhookSecret">
              <button class="btn btn-outline-dark" @click="copy(newWebhookSecret)">Copy</button>
            </div>
          </div>

          <form v-if="showWebhookForm" class="integration-form rounded border bg-light p-3 mb-4" @submit.prevent="createWebhook">
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label">Name</label>
                <input v-model.trim="webhookForm.name" maxlength="100" required class="form-control" placeholder="Automation endpoint">
              </div>
              <div class="col-md-8">
                <label class="form-label">Endpoint URL</label>
                <input v-model.trim="webhookForm.url" type="url" required class="form-control" placeholder="https://example.com/webhooks/gstocky">
              </div>
            </div>
            <div class="mt-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <label class="form-label mb-0">Events</label>
                <button type="button" class="btn btn-link btn-sm p-0" @click="toggleAllEvents">{{ allEventsSelected ? 'Clear all' : 'Select all' }}</button>
              </div>
              <div class="event-picker rounded border bg-white p-3">
                <div v-for="event in eventOptions" :key="event" class="form-check event-option">
                  <input :id="`event-${event}`" v-model="webhookForm.events" :value="event" type="checkbox" class="form-check-input">
                  <label :for="`event-${event}`" class="form-check-label font-monospace small">{{ event }}</label>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-end gap-2 mt-3">
              <button type="button" class="btn btn-light border" @click="showWebhookForm = false">Cancel</button>
              <button class="btn btn-success" :disabled="savingWebhook || !webhookForm.events.length">
                <span v-if="savingWebhook" class="spinner-border spinner-border-sm me-2"></span>Add endpoint
              </button>
            </div>
          </form>

          <div v-if="webhooks.length" class="d-grid gap-3">
            <div v-for="webhook in webhooks" :key="webhook.id" class="webhook-row rounded border p-3">
              <div class="d-flex flex-wrap justify-content-between gap-3">
                <div class="min-w-0">
                  <div class="d-flex align-items-center gap-2">
                    <strong>{{ webhook.name }}</strong>
                    <span class="badge" :class="webhook.enabled ? 'bg-success-subtle text-success' : 'bg-light text-muted'">{{ webhook.enabled ? 'Active' : 'Paused' }}</span>
                    <span v-if="webhook.last_delivery_status" class="badge" :class="webhook.last_delivery_status === 'success' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">Last delivery: {{ webhook.last_delivery_status }}</span>
                  </div>
                  <div class="font-monospace small text-break mt-1">{{ webhook.url }}</div>
                  <div class="small text-muted mt-2">{{ webhook.events.length }} events · Secret {{ webhook.secret_hint }} · Last delivery {{ formatDate(webhook.last_delivery_at) }}</div>
                  <div v-if="webhook.last_delivery_error" class="small text-danger mt-1">{{ webhook.last_delivery_error }}</div>
                </div>
                <div class="d-flex align-items-start gap-2">
                  <button class="btn btn-outline-primary btn-sm" :disabled="testingWebhook === webhook.id" @click="testWebhook(webhook)">Test</button>
                  <button class="btn btn-outline-secondary btn-sm" @click="toggleWebhook(webhook)">{{ webhook.enabled ? 'Pause' : 'Enable' }}</button>
                  <button class="btn btn-outline-secondary btn-sm" @click="rotateSecret(webhook)">Rotate secret</button>
                  <button class="btn btn-outline-danger btn-sm" @click="deleteWebhook(webhook)">Delete</button>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="text-muted mb-0">No webhook endpoints configured.</p>

          <div class="small text-muted mt-4">
            Verify <code>X-GStocky-Signature</code> as HMAC-SHA256 of <code>timestamp.raw_body</code> using your signing secret. The timestamp is sent in <code>X-GStocky-Timestamp</code>.
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import helper from '../helper'
import ConnectorBrandIcon from './ConnectorBrandIcon.vue'

export default {
  name: 'DeveloperIntegrations',
  components: { ConnectorBrandIcon },
  data() {
    return {
      loading: true,
      tokens: [],
      webhooks: [],
      eventOptions: [],
      showTokenForm: false,
      showWebhookForm: false,
      savingToken: false,
      savingWebhook: false,
      testingWebhook: null,
      newToken: '',
      newWebhookSecret: '',
      tokenForm: { name: '', rate_limit_per_minute: 60, expires_at: '', write: false },
      webhookForm: { name: '', url: '', events: ['client.created', 'order.created', 'invoice.created'] }
    }
  },
  computed: {
    apiBase() {
      return `${process.env.VUE_APP_API}/v1`
    },
    mcpUrl() {
      return process.env.VUE_APP_MCP_URL || 'https://api-v2.gstocky.com/mcp'
    },
    allEventsSelected() {
      return this.eventOptions.length > 0 && this.webhookForm.events.length === this.eventOptions.length
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const [config, tokens, webhooks] = await Promise.all([
          helper.customGet(`${process.env.VUE_APP_API}/developer/config`),
          helper.customGet(`${process.env.VUE_APP_API}/developer/tokens`),
          helper.customGet(`${process.env.VUE_APP_API}/developer/webhooks`)
        ])
        this.eventOptions = config.data.webhook_events || []
        this.tokens = tokens.data.data || []
        this.webhooks = webhooks.data.data || []
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Could not load developer integrations')
      } finally {
        this.loading = false
      }
    },
    async createToken() {
      this.savingToken = true
      this.newToken = ''
      try {
        const response = await helper.customPost(`${process.env.VUE_APP_API}/developer/tokens`, {
          name: this.tokenForm.name,
          rate_limit_per_minute: this.tokenForm.rate_limit_per_minute,
          expires_at: this.tokenForm.expires_at || null,
          scopes: this.tokenForm.write ? ['read', 'write'] : ['read']
        })
        this.newToken = response.data.data.token
        this.tokens.unshift(response.data.data)
        this.tokenForm = { name: '', rate_limit_per_minute: 60, expires_at: '', write: false }
        this.showTokenForm = false
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Could not generate API token')
      } finally {
        this.savingToken = false
      }
    },
    async toggleToken(token, enabled) {
      try {
        const response = await helper.customPatch(`${process.env.VUE_APP_API}/developer/tokens/${token.id}`, { enabled })
        Object.assign(token, response.data.data)
      } catch (error) {
        token.enabled = !enabled
        this.$toast.error(error.response?.data?.message || 'Could not update token')
      }
    },
    async revokeToken(token) {
      const result = await this.$swal({ title: 'Revoke this API token?', text: 'Applications using it will immediately lose access.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Revoke' })
      if (!result.isConfirmed) return
      try {
        await helper.customDelete(`${process.env.VUE_APP_API}/developer/tokens/${token.id}`)
        this.tokens = this.tokens.filter(item => item.id !== token.id)
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Could not revoke token')
      }
    },
    toggleAllEvents() {
      this.webhookForm.events = this.allEventsSelected ? [] : [...this.eventOptions]
    },
    async createWebhook() {
      this.savingWebhook = true
      this.newWebhookSecret = ''
      try {
        const response = await helper.customPost(`${process.env.VUE_APP_API}/developer/webhooks`, this.webhookForm)
        this.newWebhookSecret = response.data.data.secret
        this.webhooks.unshift(response.data.data)
        this.webhookForm = { name: '', url: '', events: ['client.created', 'order.created', 'invoice.created'] }
        this.showWebhookForm = false
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Could not add webhook endpoint')
      } finally {
        this.savingWebhook = false
      }
    },
    async toggleWebhook(webhook) {
      try {
        const response = await helper.customPatch(`${process.env.VUE_APP_API}/developer/webhooks/${webhook.id}`, { enabled: !webhook.enabled })
        Object.assign(webhook, response.data.data)
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Could not update webhook')
      }
    },
    async testWebhook(webhook) {
      this.testingWebhook = webhook.id
      try {
        await helper.customPost(`${process.env.VUE_APP_API}/developer/webhooks/${webhook.id}/test`, {})
        this.$toast.success('Test webhook delivered successfully')
        await this.loadWebhooks()
      } catch (error) {
        this.$toast.error(error.response?.data?.data?.error || error.response?.data?.message || 'Test delivery failed')
        await this.loadWebhooks()
      } finally {
        this.testingWebhook = null
      }
    },
    async rotateSecret(webhook) {
      const result = await this.$swal({ title: 'Rotate signing secret?', text: 'Update the secret in the receiving platform immediately.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Rotate' })
      if (!result.isConfirmed) return
      try {
        const response = await helper.customPost(`${process.env.VUE_APP_API}/developer/webhooks/${webhook.id}/rotate-secret`, {})
        this.newWebhookSecret = response.data.data.secret
        Object.assign(webhook, response.data.data)
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Could not rotate secret')
      }
    },
    async deleteWebhook(webhook) {
      const result = await this.$swal({ title: 'Delete this webhook?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Delete' })
      if (!result.isConfirmed) return
      try {
        await helper.customDelete(`${process.env.VUE_APP_API}/developer/webhooks/${webhook.id}`)
        this.webhooks = this.webhooks.filter(item => item.id !== webhook.id)
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Could not delete webhook')
      }
    },
    async loadWebhooks() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/developer/webhooks`)
        this.webhooks = response.data.data || []
      } catch (error) {
        // Keep the current list if refreshing delivery status fails.
      }
    },
    async copy(value) {
      try {
        await navigator.clipboard.writeText(value)
        this.$toast.success('Copied to clipboard')
      } catch (error) {
        this.$toast.error('Could not copy to clipboard')
      }
    },
    formatDate(value) {
      if (!value) return 'Never'
      return new Intl.DateTimeFormat(this.$i18n.locale || 'en', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    }
  }
}
</script>

<style scoped>
.integration-form { border-color: #e2e8f0 !important; }
.api-example { background: #f8fafc; border-color: #e2e8f0 !important; overflow-x: auto; }
.api-example code { white-space: nowrap; color: #334155; }
.event-picker { display: grid; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); gap: .5rem 1rem; max-height: 260px; overflow-y: auto; }
.event-option { min-width: 0; }
.webhook-row { border-color: #e2e8f0 !important; }
.min-w-0 { min-width: 0; }
.bg-success-subtle { background: #dcfce7 !important; }
.bg-danger-subtle { background: #fee2e2 !important; }
.mcp-card { border-color: #cbdcff !important; overflow: hidden; }
.mcp-card .card-header { background: linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%) !important; }
.brand-stack { display: flex; align-items: center; padding-left: 14px; }
.brand-stack > * { margin-left: -14px; border: 3px solid #fff; }
.brand-stack > *:first-child { margin-left: 0; }
.mcp-badge { color: #174ea6; background: #dbeafe; border: 1px solid #bfdbfe; }
.mcp-url-group .form-control { min-width: 0; color: #1e3a5f; background: #f8fafc; }
.connector-guides { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
.connector-guide { padding: 1.15rem; border: 1px solid #e2e8f0; border-radius: 14px; background: #fff; }
.connector-guide-heading { display: flex; align-items: center; gap: .8rem; padding-bottom: 1rem; border-bottom: 1px solid #eef2f7; }
.connection-steps { list-style: none; margin: 1rem 0 0; padding: 0; display: grid; gap: .8rem; }
.connection-steps li { display: grid; grid-template-columns: 26px minmax(0, 1fr); gap: .65rem; align-items: start; color: #475569; font-size: .9rem; line-height: 1.45; }
.connection-steps li > span { display: grid; place-items: center; width: 24px; height: 24px; border-radius: 50%; color: #174ea6; background: #eaf2ff; font-size: .75rem; font-weight: 800; }
.mcp-capabilities { display: flex; flex-wrap: wrap; gap: .55rem; }
.mcp-capabilities span { display: inline-flex; align-items: center; gap: .4rem; padding: .45rem .65rem; border-radius: 999px; color: #334155; background: #f1f5f9; font-size: .8rem; font-weight: 600; }
@media (max-width: 767px) {
  .event-picker { grid-template-columns: 1fr; }
  .connector-guides { grid-template-columns: 1fr; }
  .mcp-url-group { display: grid; grid-template-columns: auto minmax(0, 1fr); }
  .mcp-url-group .btn { grid-column: 1 / -1; border-radius: .375rem !important; margin-top: .5rem; }
}
</style>
